/**
 * Multi-broker registry + Smart Order Router (SOR) for NigaStock.
 *
 * The broker integration was single-tenant (bamboo) in phase 1. Phase 2 adds
 * a pluggable broker registry plus a SOR that picks the venue with the best
 * expected all-in cost for a given order (price improvement, commission,
 * reliability score).
 *
 * Production architecture:
 *   - BAL /v1/brokers lists brokers the user has linked, each with its own
 *     IAM-scoped Secrets Manager entry for OAuth creds.
 *   - This client-side registry mirrors what the BAL returns so the UI can
 *     render the broker-selector pill without a round-trip. It is refreshed
 *     every 5 minutes by the broker poller.
 *   - Each broker has an adapter in services/broker-adapters/<name>. Phase 2
 *     adds Chapel Hill Denham (CHD) and Meristem alongside Bamboo. The
 *     adapter-interface contract (validators + idempotency + audit) is
 *     unchanged — only transport + mapping differ per adapter.
 *   - Smart-Order-Router selects the broker with min(totalCost). Tie-breaks
 *     use the reliabilityScore (0..1) computed by the reconciliation agent
 *     over the last 30 days of fills.
 */

export type BrokerId = 'bamboo' | 'chd' | 'meristem' | 'cordros' | 'sor';

export interface BrokerSpec {
  id: BrokerId;
  label: string;           // display name
  logoGlyph: string;       // single char/emoji for the topbar pill
  commissionBps: number;   // negotiated brokerage rate, 0.0001 = 1bp
  reliabilityScore: number; // 0..1 (1 = 100% reconciled fills last 30d)
  medianLatencyMs: number; // p50 ack latency (lower is better)
  supports: {
    EQUITY?: boolean;
    BOND?: boolean;
    ETF?: boolean;
    FX?: boolean;
  };
  /** Minimum NGN consideration per trade (some brokers won't process tiny lots). */
  minConsideration: number;
}

export const BROKERS: BrokerSpec[] = [
  { id: 'bamboo',   label: 'Bamboo',             logoGlyph: 'B', commissionBps: 135, reliabilityScore: 0.994, medianLatencyMs: 180,
    supports: { EQUITY: true, ETF: true, BOND: false }, minConsideration: 1000 },
  { id: 'chd',      label: 'Chapel Hill Denham', logoGlyph: 'C', commissionBps: 120, reliabilityScore: 0.989, medianLatencyMs: 230,
    supports: { EQUITY: true, ETF: true, BOND: true }, minConsideration: 100000 },
  { id: 'meristem', label: 'Meristem',           logoGlyph: 'M', commissionBps: 150, reliabilityScore: 0.985, medianLatencyMs: 160,
    supports: { EQUITY: true, ETF: true, BOND: true }, minConsideration: 50000 },
  { id: 'cordros',  label: 'Cordros',            logoGlyph: 'R', commissionBps: 125, reliabilityScore: 0.982, medianLatencyMs: 210,
    supports: { EQUITY: true, ETF: true, BOND: true }, minConsideration: 200000 },
];

export const SOR_BROKER: BrokerSpec = {
  id: 'sor', label: 'Smart Order Router', logoGlyph: '\u21B3',
  commissionBps: 0, reliabilityScore: 1, medianLatencyMs: 0,
  supports: { EQUITY: true, ETF: true, BOND: true, FX: true },
  minConsideration: 0,
};

export interface SorInput {
  securityType: 'EQUITY' | 'BOND' | 'ETF' | 'FX';
  considerationKobo: bigint;
}
export interface SorPick {
  broker: BrokerSpec;
  expectedCommissionKobo: bigint;
  rationale: string[];
}

/**
 * Pick the best broker for a given order.
 * Scoring: 60% all-in cost, 30% reliability, 10% latency.
 */
export function smartRoute(i: SorInput): SorPick {
  const candidates = BROKERS.filter(
    (b) => b.supports[i.securityType] && BigInt(b.minConsideration) * 100n <= i.considerationKobo,
  );
  if (candidates.length === 0) {
    return {
      broker: BROKERS[0]!,
      expectedCommissionKobo: (i.considerationKobo * BigInt(BROKERS[0]!.commissionBps)) / 10000n,
      rationale: ['Fallback: no broker satisfied constraints; defaulting to primary.'],
    };
  }
  // Compute cost + score
  const scored = candidates.map((b) => {
    const commission = (i.considerationKobo * BigInt(b.commissionBps)) / 10000n;
    // Normalise: lower commission = higher score; reliability is already 0..1;
    // lower latency = higher score (cap at 500ms).
    const minCommBps = Math.min(...candidates.map((x) => x.commissionBps));
    const commScore = minCommBps / b.commissionBps;
    const latScore = 1 - Math.min(1, b.medianLatencyMs / 500);
    const total = commScore * 0.6 + b.reliabilityScore * 0.3 + latScore * 0.1;
    return { b, commission, total };
  });
  scored.sort((a, x) => x.total - a.total);
  const winner = scored[0]!;
  const rationale = [
    `${winner.b.label} selected (score ${(winner.total * 100).toFixed(1)}/100).`,
    `Commission ${winner.b.commissionBps}bps \u2014 min of ${candidates.map((c) => c.commissionBps).join(', ')}.`,
    `Reliability ${(winner.b.reliabilityScore * 100).toFixed(1)}% over last 30d.`,
    `Median ack latency ${winner.b.medianLatencyMs}ms.`,
  ];
  return { broker: winner.b, expectedCommissionKobo: winner.commission, rationale };
}

export function brokerById(id: BrokerId): BrokerSpec | undefined {
  if (id === 'sor') return SOR_BROKER;
  return BROKERS.find((b) => b.id === id);
}
